import { BrowserRouter } from "react-router-dom";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { AdminProvider } from "./context/AdminContext";
import { DarkModeProvider } from "./context/DarkModeContext";
import { SiteContentProvider, useSiteContentContext } from "./context/SiteContentContext";
import SiteNotifications from "./components/feature/SiteNotifications";
import { useAdmin } from "./context/AdminContext";
import { useEffect } from "react";

/**
 * Fetches site content from Supabase on startup and hydrates localStorage.
 * Shows a minimal loading screen while fetching so components get real data.
 */
function SiteContentLoader({ children }: { children: React.ReactNode }) {
  const { loadState } = useSiteContentContext();
  const { reSyncFromStorage } = useAdmin();

  useEffect(() => {
    // After SiteContentProvider fetches from Supabase and writes to localStorage,
    // re-sync AdminContext so cloud-synced section visibility/order overrides defaults.
    if (loadState === "loaded") {
      reSyncFromStorage();
    }
  }, [loadState, reSyncFromStorage]);

  if (loadState === "loading") {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-pink-400 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm text-gray-400 font-medium">Loading...</span>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Wraps the app content with a mobile-width constraint when mobile preview is active.
 */
function MobilePreviewWrapper({ children }: { children: React.ReactNode }) {
  const { isAdmin, isMobilePreview } = useAdmin();

  if (isAdmin && isMobilePreview) {
    return (
      <div className="min-h-screen bg-gray-200 flex items-start justify-center py-4">
        <div className="relative">
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-bold text-gray-500 whitespace-nowrap flex items-center gap-1.5">
            <i className="ri-smartphone-line text-sky-500"></i>
            Mobile Preview (390px)
          </div>
          <div
            className="w-[390px] overflow-x-hidden overflow-y-auto bg-white rounded-2xl"
            style={{ maxHeight: "calc(100vh - 80px)", boxShadow: "0 0 0 8px #374151, 0 0 0 10px #1f2937" }}
          >
            {children}
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <DarkModeProvider>
        <AdminProvider>
          <SiteContentProvider>
            <BrowserRouter basename={__BASE_PATH__}>
              <SiteContentLoader>
                <SiteNotifications />
                <MobilePreviewWrapper>
                  <AppRoutes />
                </MobilePreviewWrapper>
              </SiteContentLoader>
            </BrowserRouter>
          </SiteContentProvider>
        </AdminProvider>
      </DarkModeProvider>
    </I18nextProvider>
  );
}

export default App;
